import React from "react";
import ReactDOM from "react-dom";
import IconButton from "@material-ui/core/IconButton";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { withStyles } from "@material-ui/core/styles";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { saveGridColumnState } from "../../components/dataGrid/gridUtilities";
import Tooltip from "../../components/common/StyledToolTip";

const styles = (theme) => ({
  root: {
    display: "block",
    float: "left",
  },
  paper: {
    backgroundColor: theme.ModalBGColor,
  },
  menuList: {
    maxHeight: "300px",
    overflow: "auto",
  },
  menuItemText: {
    fontSize: "12px",
    padding: "8px",
    color: theme.ColumnHeaderTextColor,
    fontWeight: 600,
  },
  buttonRoot: {
    margin: 0,
    padding: 0,
    color: theme.iconColor,
    backgroundColor: "transparent",
    "& $buttonRootLabel": {
      color: theme.iconColor,
    },
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  buttonActive: {
    "& $buttonRootLabel": {
      color: theme.HeaderSelectedText,
    },
  },
  buttonRootLabel: {},
  iconItem: {
    fontSize: "12px",
    paddingRight: "5px",
  },
  hideColumn: {
    // here we are hiding column using height 0 because display:"none" gives wrong destination index on DragEnd method
    visibility: "hidden",
    height: 0,
  },
  hiddenColumn: {
    opacity: 0.6,
  },
  // Above ag-Grid headers / overlays so the menu receives clicks (portal + stacking).
  columnMenuPopper: {
    zIndex: 1400,
  },
});

class MenuListComposition extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      columns: [],
    };
    this.portalItem = null;
  }

  componentDidMount() {
    this.portalItem = document.getElementById("columnDrag");
    if (!this.portalItem) {
      this.portalItem = document.createElement("div");
      this.portalItem.id = "columnDrag";
    }
    document.body.appendChild(this.portalItem);
  }

  componentWillUnmount() {
    if (this.portalItem) {
      this.portalItem.parentNode &&
        this.portalItem.parentNode.removeChild(this.portalItem);
      this.portalItem = null;
    }
  }

  shouldComponentUpdate(_nextProps, nextState){
    return this.state.open !== nextState.open;
  }

  setColumns = () => {
    if (this.props.api && this.props.api.columnController) {
      const columns = this.props.api.columnController.getAllGridColumns();
      this.setState({
        columns,
      });
    }
  };

  handleToggle = () => {
    if (!this.state.open) {
      this.setColumns();
    }
    this.setState((state) => ({ open: !state.open }));
  };

  handleClose = (event) => {
    saveGridColumnState({
      gridName: this.props.api.gridName,
      columns: this.props.api.columnController.getAllGridColumns(),
      userColumnPref: this.props.userData.userProfile?.columnPref
    });

    if (this.anchorEl.contains(event.target)) {
      return;
    }
    this.setState({ open: false });
  };

  showHideColumn = (column) => {
    column.visible = !column.visible;
    if (this.props.api && this.props.api.columnController) {
      this.props.api.columnController.setColumnVisible(
        column.colDef.field,
        column.visible
      );
      this.props.api.columnController.setColumnState(
        this.props.api.columnController.getColumnState()
      );
      if (column.visible) {
        this.props.api.columnController.setColumnWidth(
          column,
          column.colDef.minWidth || 150
        );
      }
    }
    this.forceUpdate();
  };

  onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    // if tried to move a column beyond locked columns, move it as the first movable column, this behaviour will be consistent with what we observe in grid
    const firstMovableColumn = this.props.api.columnController.columnApi
      .getAllColumns()
      .findIndex((col) => !col.lockPosition);
    let destinationIndex = result.destination.index;
    if (destinationIndex < firstMovableColumn) {
      destinationIndex = firstMovableColumn;
    }

    this.props.api.columnController.columnApi.moveColumnByIndex(
      result.source.index,
      destinationIndex
    );
    this.setColumns();
  };

  getItemStyle = (draggableStyle, lockPosition) => {
    const transformPosition = lockPosition ? { transform: "0px" } : {}; // disabled moving of locked columns when a column is dragged over them
    return {
      ...draggableStyle,
      ...transformPosition,
    };
  };

  // the top value for the item is calculated with respect to the body, so rendering it as portal calculates the correct top value
  getItem = (column, provided, snapshot, classes) => {
    const item = (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={`${
          column.colDef.isInvisibleInShowHide ? classes.hideColumn : ""
        }`}
        style={this.getItemStyle(
          provided.draggableProps.style,
          column.colDef.lockPosition
        )}
      >
        <MenuItem
          classes={{
            root: `${classes.menuItemText} ${
              column.visible ? "" : classes.hiddenColumn
            }`,
          }}
          onClick={() => this.showHideColumn(column)}
        >
          {column.visible ? (
            <Visibility classes={{ root: classes.iconItem }} />
          ) : (
            <VisibilityOff classes={{ root: classes.iconItem }} />
          )}
          {column.colDef.headerName}
        </MenuItem>
      </div>
    );

    if (snapshot.isDragging) {
      return ReactDOM.createPortal(item, this.portalItem);
    }
    return item;
  };

  render() {
    const { classes } = this.props;
    const { open } = this.state;
    return (
      <div className={classes.root}>
        <div>
          <Tooltip title="Show/Hide columns">
            <IconButton
              buttonRef={(node) => {
                this.anchorEl = node;
              }}
              aria-owns={open ? "menu-list-grow" : undefined}
              aria-haspopup="true"
              onClick={this.handleToggle}
              classes={{
                root: classes.buttonRoot,
                label: classes.buttonRootLabel,
              }}
              className={open ? classes.buttonActive : ""}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <Popper
            open={open}
            anchorEl={this.anchorEl}
            transition
            placement="bottom-end"
            className={classes.columnMenuPopper}
          >
            {({ TransitionProps, placement }) => (
              <Grow {...TransitionProps} id="menu-list-grow">
                <Paper classes={{ root: classes.paper }}>
                  <ClickAwayListener onClickAway={this.handleClose}>
                    <MenuList classes={{ root: classes.menuList }}>
                      <DragDropContext onDragEnd={this.onDragEnd}>
                        <Droppable droppableId="droppable">
                          {(provided) => (
                            <div ref={provided.innerRef}>
                              {this.state.columns &&
                                this.state.columns.map((column, index) => {
                                  const headerId = `${column.colDef.headerName}_${index}`;
                                  return (
                                    <Draggable
                                      key={headerId}
                                      draggableId={
                                        column.colDef.headerName
                                          ? headerId
                                          : `draggableId_${index}`
                                      }
                                      index={index}
                                      isDragDisabled={
                                        column.colDef.lockPosition
                                      }
                                    >
                                      {(provided, snapshot) =>
                                        this.getItem(
                                          column,
                                          provided,
                                          snapshot,
                                          classes
                                        )
                                      }
                                    </Draggable>
                                  );
                                })}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(MenuListComposition);
